import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Server, Trash2, MapPin, Gauge } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { value: "running",     label: "Running",     color: "bg-emerald-500 text-white" },
  { value: "idle",        label: "Idle",        color: "bg-slate-400 text-white" },
  { value: "setup",       label: "Setup",       color: "bg-amber-500 text-white" },
  { value: "down",        label: "Down",        color: "bg-red-600 text-white" },
  { value: "maintenance", label: "Maintenance", color: "bg-purple-600 text-white" },
];
const DOWN_CATEGORIES = [
  { value: "breakdown",   label: "Breakdown" },
  { value: "changeover",  label: "Changeover" },
  { value: "material",    label: "Material starvation" },
  { value: "quality",     label: "Quality hold" },
  { value: "planned",     label: "Planned" },
  { value: "other",       label: "Other" },
];

const EMPTY = { name: "", code: "", machine_type: "", location: "", ideal_cycle_seconds: "", ideal_output_per_hour: "", status: "idle", notes: "" };

export default function GmesMachines() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [statusFor, setStatusFor] = useState(null); // machine object
  const [statusForm, setStatusForm] = useState({ status: "idle", reason: "", downtime_category: "breakdown" });

  const load = () => api.get("/gmes/machines").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await api.post("/gmes/machines", {
        ...form,
        ideal_cycle_seconds: form.ideal_cycle_seconds ? parseFloat(form.ideal_cycle_seconds) : null,
        ideal_output_per_hour: form.ideal_output_per_hour ? parseFloat(form.ideal_output_per_hour) : null,
      });
      toast.success("Machine added");
      setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this machine?")) return;
    await api.delete(`/gmes/machines/${id}`);
    load();
  };

  const openStatus = (m) => {
    setStatusFor(m);
    setStatusForm({ status: m.status || "idle", reason: "", downtime_category: "breakdown" });
  };

  const saveStatus = async () => {
    if (!statusFor) return;
    try {
      await api.patch(`/gmes/machines/${statusFor.id}/status`, statusForm);
      toast.success(`Status set to ${statusForm.status}`);
      setStatusFor(null);
      load();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">
            <Link to="/app/shopfloor" className="hover:text-primary">Shop Floor</Link> / Machines
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-2" data-testid="gmes-machines-title">
            Machines &amp; Assets
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Track every asset on the floor. One-tap status changes; downtime events open and close automatically.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="add-machine-btn">
          <Plus className="w-4 h-4 mr-2" /> Add machine
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-16 text-center bg-card/40">
          <Server className="w-8 h-8 mx-auto text-muted-foreground" />
          <div className="font-display text-lg mt-4">No machines yet</div>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Add your first press, line, cell, or workstation. Machines feed OEE, downtime and production tracking.
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((m) => {
            const status = STATUSES.find((s) => s.value === m.status) || STATUSES[1];
            return (
              <div key={m.id} className="border border-border bg-card rounded-sm p-5" data-testid={`machine-${m.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display font-bold text-lg leading-tight">{m.name}</div>
                    {m.code && <div className="text-xs font-mono-plex text-muted-foreground mt-0.5">{m.code}</div>}
                  </div>
                  <button onClick={() => remove(m.id)} className="text-muted-foreground hover:text-destructive" data-testid={`del-machine-${m.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <button
                  onClick={() => openStatus(m)}
                  className={`mt-3 inline-flex items-center gap-2 rounded-sm px-3 py-1.5 text-xs font-mono-plex uppercase tracking-widest ${status.color}`}
                  data-testid={`machine-status-${m.id}`}
                >
                  <span className="w-1.5 h-1.5 bg-white/80 rounded-full" /> {status.label}
                </button>
                {m.status_reason && <div className="text-xs text-muted-foreground mt-2 italic">"{m.status_reason}"</div>}
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  {m.machine_type && <div><span className="opacity-70">Type:</span> {m.machine_type}</div>}
                  {m.location && <div className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {m.location}</div>}
                  {m.ideal_cycle_seconds != null && <div className="inline-flex items-center gap-1"><Gauge className="w-3 h-3" /> {m.ideal_cycle_seconds}s cycle</div>}
                  {m.ideal_output_per_hour != null && <div>{m.ideal_output_per_hour}/hr target</div>}
                </div>
                {m.notes && <p className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground leading-relaxed">{m.notes}</p>}
              </div>
            );
          })}
        </div>
      )}

      {/* Add machine dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl rounded-sm">
          <DialogHeader>
            <div className="overline text-accent">◉ NEW MACHINE</div>
            <DialogTitle className="font-display text-2xl font-bold tracking-tight">Add asset</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Name *</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="machine-name-input" required />
              </div>
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Code</label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="rounded-sm" placeholder="P-01" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Type</label>
                <Input value={form.machine_type} onChange={(e) => setForm({ ...form, machine_type: e.target.value })} className="rounded-sm" placeholder="Flexo press, CNC, injection…" />
              </div>
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Location</label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-sm" placeholder="Line A" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Ideal cycle (sec)</label>
                <Input type="number" step="0.1" value={form.ideal_cycle_seconds} onChange={(e) => setForm({ ...form, ideal_cycle_seconds: e.target.value })} className="rounded-sm" />
              </div>
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Ideal output / hr</label>
                <Input type="number" step="1" value={form.ideal_output_per_hour} onChange={(e) => setForm({ ...form, ideal_output_per_hour: e.target.value })} className="rounded-sm" />
              </div>
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-1.5">Notes</label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm resize-none" />
            </div>
            <Button type="submit" disabled={saving} className="w-full h-11 rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="save-machine-btn">
              {saving ? "Saving…" : "Save machine"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Status change dialog */}
      <Dialog open={!!statusFor} onOpenChange={(v) => !v && setStatusFor(null)}>
        <DialogContent className="sm:max-w-md rounded-sm">
          <DialogHeader>
            <div className="overline text-accent">◉ STATUS UPDATE</div>
            <DialogTitle className="font-display text-xl font-bold">{statusFor?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-5 gap-1.5">
              {STATUSES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setStatusForm({ ...statusForm, status: s.value })}
                  className={`rounded-sm px-2 py-2 text-[11px] font-mono-plex uppercase tracking-widest transition ${
                    statusForm.status === s.value ? s.color : "bg-secondary text-foreground hover:bg-secondary/70"
                  }`}
                  data-testid={`pick-status-${s.value}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-1.5">Reason / note</label>
              <Input value={statusForm.reason} onChange={(e) => setStatusForm({ ...statusForm, reason: e.target.value })} className="rounded-sm" placeholder="Bearing seized, changeover to job 1234…" />
            </div>
            {statusForm.status === "down" && (
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Downtime category</label>
                <Select value={statusForm.downtime_category} onValueChange={(v) => setStatusForm({ ...statusForm, downtime_category: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOWN_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={saveStatus} className="w-full h-11 rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="confirm-status-btn">
              Update status
            </Button>
            <div className="text-[11px] text-muted-foreground text-center">
              Setting to <Badge className="rounded-sm mx-0.5 bg-red-600 text-white hover:bg-red-600">Down</Badge> auto-opens a downtime event. Setting to anything else closes any open downtime.
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
